import { MapPin, Phone, Mail, Clock, Send, Users, CheckCircle } from 'lucide-react';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { API_ENDPOINTS } from '../../utils/apiConfig';

export default function MainContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Inquiry',
    message: '',
    preferredContact: 'email',
    interestedServices: [] as string[]
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedLocation = 0;
  const [errors, setErrors] = useState<Record<string, string>>({});

  const locations = [
    {
      name: 'Corporate Office - Udaipur',
      address:
        'First Floor, Above Arbuda Electric, Behind Celebration Mall, Plot No. 5, Garh Magri, Ratneshwar Colony, Pulla Bhuwana, Udaipur, Rajasthan 313001',
      phone: '+91 88299 12389',
      email: 'cto.metafit@gmail.com',
      hours: {
        weekdays: '6:00 AM - 9:00 PM',
        saturday: '6:00 AM - 9:00 PM',
        sunday: '8:00 AM - 6:00 PM'
      },
      image:
        'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      specialties: ['Hatha Yoga', 'Meditation', 'Breathwork', 'Corporate Wellness']
    }
  ];

  const handleInputChange = (e: any) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData((prev: any) => ({
        ...prev,
        interestedServices: checked
          ? [...prev.interestedServices, value]
          : prev.interestedServices.filter((service: any) => service !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.phone && !/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        interestedServices: formData.interestedServices,
        preferredContact: formData.preferredContact,
        submittedAt: new Date().toISOString()
      };

      const response = await fetch(API_ENDPOINTS.CONTACT_SUBMIT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const data = await response.json();

      if (data.success) {
        toast.success(data.message || "Thank you! We'll get back to you within 24 hours.");
        setIsSubmitted(true);

        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: 'General Inquiry',
          message: '',
          preferredContact: 'email',
          interestedServices: []
        });

        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        toast.error(data.message || 'Failed to submit form. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    'Group Yoga Classes',
    'Private Sessions',
    'Meditation Classes',
    'Corporate Wellness',
    'Teacher Training',
    'Retreats & Workshops',
    'Nutrition Counseling',
    'Therapeutic Yoga'
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 sm:pt-3 pb-12 lg:pb-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start">
        {/* Contact Form */}
        <div className="rounded-2xl border border-slate-200/90 bg-white p-6 sm:p-8 lg:p-10 shadow-sm hover:shadow-md transition-shadow">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200/70 mb-3 shadow-2xs">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
            <span>Drop us a line</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-2">
            Let&apos;s talk about your wellness goals
          </h2>
          <p className="text-sm text-slate-500 leading-relaxed mb-6">
            Tell us what you need and our team will guide you to the right class, consultant, or program.
          </p>

          {isSubmitted && (
            <div className="mb-6 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/80 p-4 text-emerald-800 text-sm">
              <CheckCircle className="text-emerald-600 shrink-0" size={18} />
              <span>Thank you! We&apos;ll get back to you within 24 hours.</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full rounded-xl border bg-slate-50/60 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-slate-800 focus:ring-4 focus:ring-slate-800/10 outline-none transition-all ${
                    errors.name ? 'border-red-500' : 'border-slate-200'
                  }`}
                  placeholder="Your full name"
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full rounded-xl border bg-slate-50/60 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-slate-800 focus:ring-4 focus:ring-slate-800/10 outline-none transition-all ${
                    errors.email ? 'border-red-500' : 'border-slate-200'
                  }`}
                  placeholder="your.email@example.com"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="phone" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData(prev => ({
                      ...prev,
                      phone: value
                    }));
                    if (errors.phone) {
                      setErrors(prev => ({
                        ...prev,
                        phone: ''
                      }));
                    }
                  }}
                  className={`w-full rounded-xl border bg-slate-50/60 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-slate-800 focus:ring-4 focus:ring-slate-800/10 outline-none transition-all ${
                    errors.phone ? 'border-red-500' : 'border-slate-200'
                  }`}
                  placeholder="+91 12345 67890"
                  maxLength={10}
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              <div>
                <label htmlFor="subject" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Subject
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm text-slate-900 focus:bg-white focus:border-slate-800 focus:ring-4 focus:ring-slate-800/10 outline-none transition-all cursor-pointer"
                >
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Class Booking">Class Booking</option>
                  <option value="Private Session">Private Session</option>
                  <option value="Corporate Wellness">Corporate Wellness</option>
                  <option value="Teacher Training">Teacher Training</option>
                  <option value="Retreat Information">Retreat Information</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                Interested Services (Optional)
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {services.map((service) => (
                  <label
                    key={service}
                    className="flex items-center gap-2.5 rounded-xl border border-slate-200/80 bg-slate-50/50 hover:bg-slate-100/70 p-2.5 sm:p-3 text-xs sm:text-sm text-slate-700 transition-colors cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      value={service}
                      onChange={handleInputChange}
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span>{service}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                value={formData.message}
                onChange={handleInputChange}
                className={`w-full rounded-xl border bg-slate-50/60 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-slate-800 focus:ring-4 focus:ring-slate-800/10 outline-none transition-all resize-y ${
                  errors.message ? 'border-red-500' : 'border-slate-200'
                }`}
                placeholder="Tell us how we can help you on your wellness journey..."
              />
              {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-black text-white px-6 py-3.5 font-semibold text-sm sm:text-base shadow-sm hover:shadow transition-all duration-200 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send size={18} />
              <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
            </button>
          </form>
        </div>

        {/* Location & Contact Information */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200/90 bg-white p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200/70 mb-3 shadow-2xs">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
              <span>Our Office</span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-2">
              Visit Our Office
            </h2>
            <p className="text-sm text-slate-500 leading-relaxed mb-6">
              Reach out to our Udaipur team or explore the location details below.
            </p>

            <div className="overflow-hidden rounded-xl border border-slate-100 shadow-xs mb-6">
              <img
                src={locations[selectedLocation].image}
                alt={locations[selectedLocation].name}
                className="w-full h-52 sm:h-60 object-cover"
              />
            </div>

            <h3 className="text-xl font-bold text-slate-900 mb-5">
              {locations[selectedLocation].name}
            </h3>

            <div className="space-y-4">
              <div className="flex items-start gap-3.5">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0 border border-emerald-100">
                  <MapPin size={18} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-0.5">Address</p>
                  <p className="text-sm text-slate-700 leading-relaxed">{locations[selectedLocation].address}</p>
                </div>
              </div>

              <div className="flex items-start gap-3.5">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0 border border-emerald-100">
                  <Phone size={18} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-0.5">Phone</p>
                  <a
                    href="tel:+918829912389"
                    className="text-sm font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
                  >
                    {locations[selectedLocation].phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3.5">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0 border border-emerald-100">
                  <Mail size={18} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-0.5">Email</p>
                  <a
                    href={`mailto:${locations[selectedLocation].email}`}
                    className="text-sm font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
                  >
                    {locations[selectedLocation].email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3.5">
                <div className="w-9 h-9 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center shrink-0 border border-sky-100">
                  <Clock size={18} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-0.5">Hours</p>
                  <div className="text-sm text-slate-700 space-y-0.5">
                    <p>Mon-Fri: {locations[selectedLocation].hours.weekdays}</p>
                    <p>Saturday: {locations[selectedLocation].hours.saturday}</p>
                    <p>Sunday: {locations[selectedLocation].hours.sunday}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3.5 pt-1">
                <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-800 flex items-center justify-center shrink-0 border border-amber-100">
                  <Users size={18} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Specialties</p>
                  <div className="flex flex-wrap gap-1.5">
                    {locations[selectedLocation].specialties.map((specialty, idx) => (
                      <span
                        key={idx}
                        className="rounded-lg border border-slate-200/80 bg-slate-100/70 px-2.5 py-1 text-xs font-medium text-slate-700"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
